import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, FloppyDisk, Trash, X, Plus, Phone, EnvelopeSimple,
  VideoCamera, NotePencil, Clock, Link as LinkIcon, ArrowRight, User,
  ChatCircleDots
} from '@phosphor-icons/react';
import {
  ACTIVITY_ICONS,
  ACTIVITY_COLORS_BADGE as ACTIVITY_COLORS,
  CALL_OUTCOMES,
  RELATIONSHIP_TYPES,
} from '../constants/contactActivity';

export default function ContactDetailPage() {
  const { statuses: statusOptions } = useStatusOptions();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm] = useState({
    first_name: '', last_name: '', company: '', email: '', phone: '',
    address_street: '', address_city: '', address_state: '', address_zip: '', address_country: '',
    status: '', tags: [], notes: '', custom_fields: {},
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Activity state
  const [activities, setActivities] = useState([]);
  const [activityDialog, setActivityDialog] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'note', title: '', content: '', duration_minutes: 0, outcome: '', direction: '' });

  // Relationship state
  const [relationships, setRelationships] = useState([]);
  const [relDialog, setRelDialog] = useState(false);
  const [relForm, setRelForm] = useState({ to_contact_id: '', relationship_type: 'colleague', notes: '' });
  const [relSearch, setRelSearch] = useState('');
  const [relSearchResults, setRelSearchResults] = useState([]);
  const [relSelectedContact, setRelSelectedContact] = useState(null);

  useEffect(() => {
    if (!isNew) {
      Promise.all([
        api.get(`/contacts/${id}`),
        api.get(`/contacts/${id}/activities`),
        api.get(`/contacts/${id}/relationships`),
      ]).then(([contactRes, activitiesRes, relsRes]) => {
        const d = contactRes.data;
        setForm({
          first_name: d.first_name || '', last_name: d.last_name || '',
          company: d.company || '', email: d.email || '', phone: d.phone || '',
          address_street: d.address_street || '', address_city: d.address_city || '',
          address_state: d.address_state || '', address_zip: d.address_zip || '',
          address_country: d.address_country || '', status: d.status || '',
          tags: d.tags || [], notes: d.notes || '', custom_fields: d.custom_fields || {},
        });
        setActivities(activitiesRes.data);
        setRelationships(relsRes.data);
      }).catch(() => setError('Contact not found'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) updateField('tags', [...form.tags, tag]);
    setTagInput('');
  };

  const removeTag = (tag) => updateField('tags', form.tags.filter(t => t !== tag));

  const handleSave = async () => {
    if (!form.first_name.trim()) { setError('First name is required'); return; }
    setSaving(true); setError('');
    try {
      if (isNew) {
        const { data } = await api.post('/contacts', form);
        navigate(`/contacts/${data.contact_id}`, { replace: true });
      } else {
        await api.put(`/contacts/${id}`, form);
        navigate('/contacts');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this contact?')) return;
    try { await api.delete(`/contacts/${id}`); navigate('/contacts'); }
    catch { setError('Failed to delete'); }
  };

  // ── Activity methods ──
  const addActivity = async () => {
    if (!activityForm.title.trim()) return;
    try {
      const { data } = await api.post(`/contacts/${id}/activities`, activityForm);
      setActivities(prev => [data, ...prev]);
      setActivityDialog(false);
      setActivityForm({ activity_type: 'note', title: '', content: '', duration_minutes: 0, outcome: '', direction: '' });
    } catch (err) { console.error('Failed to add activity', err); }
  };

  const deleteActivity = async (activityId) => {
    try {
      await api.delete(`/activities/${activityId}`);
      setActivities(prev => prev.filter(a => a.activity_id !== activityId));
    } catch (err) { console.error('Failed to delete activity', err); }
  };

  // ── Relationship methods ──
  const searchRelContacts = async (q) => {
    setRelSearch(q);
    if (q.length < 2) { setRelSearchResults([]); return; }
    try {
      const { data } = await api.get('/contacts/search/quick', { params: { q } });
      setRelSearchResults(data.filter(c => c.contact_id !== id));
    } catch { setRelSearchResults([]); }
  };

  const addRelationship = async () => {
    if (!relForm.to_contact_id) return;
    try {
      const { data } = await api.post(`/contacts/${id}/relationships`, relForm);
      setRelationships(prev => [...prev, data]);
      setRelDialog(false);
      setRelForm({ to_contact_id: '', relationship_type: 'colleague', notes: '' });
      setRelSelectedContact(null);
    } catch (err) { console.error('Failed to add relationship', err); }
  };

  const deleteRelationship = async (relId) => {
    try {
      await api.delete(`/relationships/${relId}`);
      setRelationships(prev => prev.filter(r => r.relationship_id !== relId));
    } catch (err) { console.error('Failed to delete relationship', err); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="contact-detail-loading">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="contact-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} className="rounded-sm gap-1" data-testid="back-to-contacts-btn">
            <ArrowLeft size={16} /> Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#111827]" data-testid="contact-detail-title">
            {isNew ? 'New Contact' : `${form.first_name} ${form.last_name}`}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-[#DC2626] border-red-200 hover:bg-red-50 rounded-sm gap-1" data-testid="delete-contact-btn">
              <Trash size={14} weight="bold" /> Delete
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm gap-1" data-testid="save-contact-btn">
            <FloppyDisk size={16} weight="bold" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="contact-error">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column: Contact Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-4">Basic Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">First Name *</Label>
                <Input value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} className="mt-1 rounded-sm" data-testid="first-name-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Last Name</Label>
                <Input value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} className="mt-1 rounded-sm" data-testid="last-name-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Company</Label>
                <Input value={form.company} onChange={(e) => updateField('company', e.target.value)} className="mt-1 rounded-sm" data-testid="company-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Status</Label>
                <Select value={form.status} onValueChange={(v) => updateField('status', v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    {form.status && !statusOptions.some((s) => s.value === form.status) && (
                      <SelectItem value={form.status}>{form.status}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Notes — ACT!-style: high on the page, right under Basic Info */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-4">Notes</h3>
            <Textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)} rows={3} placeholder="Add notes..." className="rounded-sm" data-testid="notes-textarea" />
          </div>

          {/* Contact Info */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-4">Contact Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} className="mt-1 rounded-sm" data-testid="email-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Phone</Label>
                <Input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} className="mt-1 rounded-sm" data-testid="phone-input" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-4">Address</h3>
            <div className="space-y-3">
              <Input value={form.address_street} onChange={(e) => updateField('address_street', e.target.value)} placeholder="Street" className="rounded-sm" data-testid="street-input" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Input value={form.address_city} onChange={(e) => updateField('address_city', e.target.value)} placeholder="City" className="rounded-sm" data-testid="city-input" />
                <Input value={form.address_state} onChange={(e) => updateField('address_state', e.target.value)} placeholder="State" className="rounded-sm" data-testid="state-input" />
                <Input value={form.address_zip} onChange={(e) => updateField('address_zip', e.target.value)} placeholder="ZIP" className="rounded-sm" data-testid="zip-input" />
                <Input value={form.address_country} onChange={(e) => updateField('address_country', e.target.value)} placeholder="Country" className="rounded-sm" data-testid="country-input" />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-4">Tags</h3>
            <div className="flex gap-2 mb-3 flex-wrap">
              {form.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="rounded-sm gap-1 pr-1">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-[#DC2626]" data-testid={`remove-tag-${tag}`}><X size={12} /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." className="rounded-sm flex-1" data-testid="tag-input" />
              <Button variant="outline" size="sm" onClick={addTag} className="rounded-sm gap-1" data-testid="add-tag-btn"><Plus size={14} /> Add</Button>
            </div>
          </div>
        </div>

        {/* Right Column: Activity Log + Relationships */}
        {!isNew && (
          <div className="space-y-4">
            {/* Activity Log */}
            <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="activity-log-section">
              <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Activity Log</h3>
                <Button variant="ghost" size="sm" onClick={() => setActivityDialog(true)} className="h-7 px-2 text-xs rounded-sm gap-1 text-[#002FA7]" data-testid="add-activity-btn">
                  <Plus size={12} weight="bold" /> Add
                </Button>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {activities.length === 0 ? (
                  <p className="text-xs text-[#4B5563] text-center py-8">No activities yet</p>
                ) : (
                  <div className="divide-y divide-[#E5E7EB]">
                    {activities.map((act) => {
                      const Icon = ACTIVITY_ICONS[act.activity_type] || NotePencil;
                      return (
                        <div key={act.activity_id} className="px-4 py-3 group" data-testid={`activity-${act.activity_id}`}>
                          <div className="flex items-start gap-2.5">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${ACTIVITY_COLORS[act.activity_type] || ACTIVITY_COLORS.note}`}>
                              <Icon size={13} weight="bold" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className="text-sm font-medium text-[#111827]">{act.title}</p>
                                  {/* Call outcome pill — visible right next to
                                      the title so a scan of the activity log
                                      answers "did we connect?" without clicks. */}
                                  {act.activity_type === 'call' && act.outcome && (
                                    <span
                                      className="inline-block px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide bg-green-100 text-green-800 border border-green-300 rounded-sm flex-shrink-0"
                                      data-testid={`activity-outcome-${act.activity_id}`}
                                    >
                                      {act.outcome}
                                    </span>
                                  )}
                                  {/* SMS direction arrow */}
                                  {act.activity_type === 'sms' && act.direction && (
                                    <span
                                      className={`inline-block px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide border rounded-sm flex-shrink-0 ${
                                        act.direction === 'sent'
                                          ? 'bg-teal-100 text-teal-800 border-teal-300'
                                          : 'bg-amber-100 text-amber-800 border-amber-300'
                                      }`}
                                      data-testid={`activity-direction-${act.activity_id}`}
                                    >
                                      {act.direction === 'sent' ? '→ Sent' : '← Received'}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => deleteActivity(act.activity_id)}
                                  className="opacity-0 group-hover:opacity-100 text-[#DC2626] hover:bg-red-50 p-0.5 rounded transition-opacity"
                                  data-testid={`delete-activity-${act.activity_id}`}
                                >
                                  <Trash size={12} />
                                </button>
                              </div>
                              {act.content && <p className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{act.content}</p>}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-[#4B5563]">{formatDate(act.created_at)}</span>
                                {act.duration_minutes > 0 && (
                                  <span className="text-[10px] text-[#4B5563] flex items-center gap-0.5">
                                    <Clock size={10} />{act.duration_minutes}min
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Relationships */}
            <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="relationships-section">
              <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                <h3 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Relationships</h3>
                <Button variant="ghost" size="sm" onClick={() => setRelDialog(true)} className="h-7 px-2 text-xs rounded-sm gap-1 text-[#002FA7]" data-testid="add-relationship-btn">
                  <Plus size={12} weight="bold" /> Link
                </Button>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {relationships.length === 0 ? (
                  <p className="text-xs text-[#4B5563] text-center py-8">No linked contacts</p>
                ) : (
                  <div className="divide-y divide-[#E5E7EB]">
                    {relationships.map((rel) => (
                      <div key={rel.relationship_id} className="px-4 py-2.5 flex items-center gap-2.5 group" data-testid={`relationship-${rel.relationship_id}`}>
                        <LinkIcon size={14} className="text-[#4B5563] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => navigate(`/contacts/${rel.related_contact_id}`)}
                            className="text-sm font-medium text-[#002FA7] hover:underline truncate block"
                            data-testid={`rel-link-${rel.related_contact_id}`}
                          >
                            {rel.related_contact_name}
                          </button>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-[#4B5563] capitalize">{rel.relationship_type}</span>
                            {rel.related_contact_company && (
                              <span className="text-[10px] text-[#4B5563]">&middot; {rel.related_contact_company}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteRelationship(rel.relationship_id)}
                          className="opacity-0 group-hover:opacity-100 text-[#DC2626] hover:bg-red-50 p-0.5 rounded transition-opacity"
                          data-testid={`delete-rel-${rel.relationship_id}`}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Activity Dialog */}
      <Dialog open={activityDialog} onOpenChange={setActivityDialog}>
        <DialogContent className="sm:max-w-[420px] rounded-sm border-[#E5E7EB] p-0 gap-0" data-testid="activity-dialog">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-base font-bold text-[#111827]">Log Activity</DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-4 space-y-3">
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Type</Label>
              <div className="flex gap-1.5 mt-1 flex-wrap">
                {['call', 'sms', 'email', 'meeting', 'note'].map(type => {
                  const Icon = ACTIVITY_ICONS[type];
                  const labels = { call: 'Call', sms: 'Text', email: 'Email', meeting: 'Meeting', note: 'Note' };
                  return (
                    <button
                      key={type}
                      onClick={() => setActivityForm(f => ({
                        ...f,
                        activity_type: type,
                        // Sensible defaults per type; clears fields that
                        // don't apply so we never save stale outcome/
                        // direction values on the wrong activity type.
                        direction: type === 'sms' ? (f.direction || 'sent') : '',
                        outcome: type === 'call' ? f.outcome : '',
                      }))}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                        activityForm.activity_type === type
                          ? 'bg-[#002FA7] text-white border-[#002FA7]'
                          : 'border-[#E5E7EB] text-[#4B5563] hover:bg-gray-50'
                      }`}
                      data-testid={`activity-type-${type}`}
                    >
                      <Icon size={13} weight="bold" />
                      <span>{labels[type]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Call outcome dropdown — only when logging a call. Kept short
                on purpose. Any call without an outcome is essentially just
                a "note that a call happened," which is fine but less useful
                for downstream reporting. */}
            {activityForm.activity_type === 'call' && (
              <div data-testid="activity-outcome-block">
                <Label className="text-xs font-medium text-[#4B5563]">Outcome</Label>
                <select
                  value={activityForm.outcome || ''}
                  onChange={(e) => setActivityForm(f => ({ ...f, outcome: e.target.value }))}
                  className="mt-1 w-full h-9 px-2 text-sm rounded-sm border border-[#E5E7EB] bg-white focus:outline-none focus:border-[#002FA7]"
                  data-testid="activity-outcome-select"
                >
                  <option value="">— How did the call go? —</option>
                  {CALL_OUTCOMES.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            )}
            {/* SMS direction toggle — two-button binary is faster than a
                dropdown for a yes/no field. Default is "sent" because
                users almost always click the Text button after texting,
                not while receiving. */}
            {activityForm.activity_type === 'sms' && (
              <div data-testid="activity-sms-direction-block">
                <Label className="text-xs font-medium text-[#4B5563]">Direction</Label>
                <div className="flex gap-1.5 mt-1">
                  {[
                    { v: 'sent', label: 'I texted them' },
                    { v: 'received', label: 'They texted me' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setActivityForm(f => ({ ...f, direction: opt.v }))}
                      className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                        (activityForm.direction || 'sent') === opt.v
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-[#E5E7EB] text-[#4B5563] hover:bg-gray-50'
                      }`}
                      data-testid={`activity-direction-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Title *</Label>
              <Input
                autoFocus
                value={activityForm.title}
                onChange={(e) => setActivityForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Discussed renewal terms"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="activity-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Details</Label>
              <Textarea
                value={activityForm.content}
                onChange={(e) => setActivityForm(f => ({ ...f, content: e.target.value }))}
                rows={3}
                className="mt-1 rounded-sm text-sm"
                placeholder="Optional details..."
                data-testid="activity-content-input"
              />
            </div>
            {(activityForm.activity_type === 'call' || activityForm.activity_type === 'meeting') && (
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Duration (minutes)</Label>
                <Input
                  type="number"
                  value={activityForm.duration_minutes}
                  onChange={(e) => setActivityForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 0 }))}
                  className="mt-1 rounded-sm h-9 text-sm w-24"
                  data-testid="activity-duration-input"
                />
              </div>
            )}
          </div>
          <div className="flex justify-end px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <Button onClick={addActivity} disabled={!activityForm.title.trim()} className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm text-sm h-8 px-4" data-testid="save-activity-btn">
              Log Activity
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Relationship Dialog */}
      <Dialog open={relDialog} onOpenChange={setRelDialog}>
        <DialogContent className="sm:max-w-[420px] rounded-sm border-[#E5E7EB] p-0 gap-0" data-testid="relationship-dialog">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-base font-bold text-[#111827]">Link Contact</DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-4 space-y-3">
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Search Contact</Label>
              {relSelectedContact ? (
                <div className="mt-1 flex items-center gap-2 p-2 bg-[#F3F4F6] border border-[#E5E7EB] rounded-sm">
                  <User size={14} className="text-[#4B5563]" />
                  <span className="text-sm font-medium text-[#111827] flex-1">{relSelectedContact.first_name} {relSelectedContact.last_name}</span>
                  <button onClick={() => { setRelSelectedContact(null); setRelForm(f => ({ ...f, to_contact_id: '' })); }} className="text-xs text-[#DC2626]">Remove</button>
                </div>
              ) : (
                <div className="mt-1">
                  <Input value={relSearch} onChange={(e) => searchRelContacts(e.target.value)} placeholder="Type name..." className="rounded-sm h-9 text-sm" data-testid="rel-search-input" />
                  {relSearch && relSearch.length === 1 && (
                    <p className="mt-1 text-xs text-[#6B7280] italic" data-testid="rel-search-hint-2">Keep typing — searching after 2 characters.</p>
                  )}
                  {relSearch && relSearch.length >= 2 && relSearchResults.length === 0 && (
                    <p className="mt-1 text-xs text-[#DC2626]" data-testid="rel-search-no-results-2">
                      No contacts match "{relSearch}".
                    </p>
                  )}
                  {relSearchResults.length > 0 && (
                    <div className="mt-1 border border-[#E5E7EB] rounded-sm bg-white max-h-32 overflow-y-auto">
                      {relSearchResults.map(c => (
                        <button
                          key={c.contact_id}
                          onClick={() => {
                            setRelSelectedContact(c);
                            setRelForm(f => ({ ...f, to_contact_id: c.contact_id }));
                            setRelSearchResults([]);
                            setRelSearch('');
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F3F4F6]"
                          data-testid={`rel-option-${c.contact_id}`}
                        >
                          <span className="font-medium">{c.first_name} {c.last_name}</span>
                          {c.company && <span className="text-xs text-[#4B5563] ml-1">({c.company})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Relationship Type</Label>
              <Select value={relForm.relationship_type} onValueChange={(v) => setRelForm(f => ({ ...f, relationship_type: v }))}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="rel-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_TYPES.map(t => <SelectItem key={t} value={t}><span className="capitalize">{t.replace('-', ' ')}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Notes</Label>
              <Input value={relForm.notes} onChange={(e) => setRelForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" className="mt-1 rounded-sm h-9 text-sm" data-testid="rel-notes-input" />
            </div>
          </div>
          <div className="flex justify-end px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <Button onClick={addRelationship} disabled={!relForm.to_contact_id} className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm text-sm h-8 px-4" data-testid="save-relationship-btn">
              Link Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
